<template>
  <component
    role="group"
    :is="userIsTabbing  ? 'a' : 'div'"
    v-if="model && currentVariant"
    :href="userIsTabbing ? link : ''"
    class="productcard"
    :class="{ 'grid-12': miniWishlistCard }"
    @mouseenter="mouseOver = true"
    @mouseleave="mouseOver = false"
  >
    <section
      class="productcard-section-top -p-relative -handle"
      :class="{ 'g-3': miniWishlistCard }"
    >
      <WishlistButton
        v-if="miniCard && outletAccess && !archived && !wishlistCard"
        class="productcard-heart m-r-12 m-t-12"
        :active="hasSizeOnWishlist"
        :noText="true"
        @on-clicked="openWishlistModal"
      />
      <ProductTopSlider
        v-if="!reloadImage"
        :variant="currentVariant"
        :variantIndex="colorVariantIndex"
        :allVariants="variants"
        :ariaLabel="`${model.Concept} ${model.Name}`"
        :gender="model.Gender"
        :colorSliderVariants="miniCard && !miniWishlistCard ? variants : null"
        :showControls="(mouseOver || miniCard) && !miniWishlistCard"
        :forcePackShot="forcePackShot"
        :cols="cols"
        :nativeSlider="miniCard && !miniWishlistCard"
        :showPackShotToggler="false"
        :deactivateMobileSwipe="deactivateMobileSwipe"
        :invisibleNavBackground="true"
        :fullScreen="false"
        :href="!userIsTabbing ? link : ''"
        :imageSettings="imageSettings"
        :noLazyImage="noLazyImage"
        :useLookImages="featureLookImages && productHasLookImages"
        @on-goto-page="trackClick"
        @on-color-index-changed="colorVariantIndex = $event"
      />
    </section>

    <!-- Image Overlays -->
    <div aria-hidden class="overlay-bounding-box" :class="{ 'd-none': miniWishlistCard }">
      <div v-if="mouseOver">
        <slot name="overlayMouseover" />
      </div>

      <!-- Size Selector Overlay -->
      <SelectSizeModule
        v-model="sizeSelectorOverlay"
        :modal="miniCard"
        :model="model"
        :currentVariant="currentVariant"
        :sizes="currentVariantSizes"
        :fit="model.Fit"
        @updatedSelectedVariant="updateSelectedSizeVariant"
      />
      <SelectColorModule
        v-if="wishlistCard"
        v-model="colorSelectorOverlay"
        :modal="miniCard"
        :variants="variants"
        :colorIndex="colorVariantIndex"
        :outlet="outlet"
        :overlay="true"
        @updatedSelectedVariant="updateSelectedColorVariant"
      />
      <!-- Get Notified Overlay -->
      <div class="overlay-container" v-if="showNotifyOverlay && !miniCard">
        <button class="close" @click.prevent="showNotifyOverlay = false">
          <SvgComponent :icon="{ name: 'close' }" />
        </button>
        <Notify
          class="m-t-24"
          :model="model"
          :currentVariant="currentVariant"
          :selectedSizeObject="selectedSizeObject"
          :availableFrom="model.AvailableFrom"
        />
      </div>
    </div>
    <section
      class="productcard-section-bottom -p-relative"
      :class="[
        miniCard ? 'm-t-6' : 'm-t-12',
        { '-outlet': outlet, 'g-9': miniWishlistCard },
      ]"
    >
      <ProductCardFrontDeck
        v-if="!wishlistCard"
        v-show="(!mouseOver && !mixin_addToBag_adding) || miniCard"
        :index="index"
        :model="model"
        :currentVariant="currentVariant"
        :productName="productName"
        :variantCount="variants.length"
        :isPrePurchase="isPrePurchase"
        :outletAccess="outletAccess"
        :miniCard="miniCard"
        :href="!userIsTabbing ? link : ''"
        @on-goto-page="trackClick"
        @on-quick-buy="sizeSelectorOverlay = 'bag'"
        @on-notify="sizeSelectorOverlay = 'notify'"
        @on-prepurchase="showPrePurchaseConfirm = true"
      />
      <ProductCardBackDeck
        v-if="!wishlistCard && !miniCard"
        :isVisible="mouseOver || mixin_addToBag_adding"
        :model="model"
        :productName="productName"
        :href="link"
        :addingToBag="mixin_addToBag_adding"
        :sizeWarning="sizeWarning"
        :currentVariant="currentVariant"
        :sizeVariantIndex="sizeVariantIndex"
        :isPrePurchase="isPrePurchase"
        :hideSizes="sizeSelectorOverlay !== 'hidden'"
        :variants="variants"
        :fontColorOverride="fontColorOverride"
        :outletAccess="outletAccess"
        :outlet="outlet"
        :showQuickBuy="showQuickBuy"
        @on-quick-buy="quickAddDesktop"
        @on-notify="showNotifyOverlay = true"
        @on-prepurchase="showPrePurchaseConfirm = true"
        @on-color-change="colorVariantIndex = $event"
        @on-size-change="sizeVariantIndex = $event"
      >
        <template v-slot:wishlist>
          <WishlistButton
            class="m-r-12"
            v-if="outletAccess && !archived"
            :active="hasSizeOnWishlist"
            @on-clicked="openWishlistModal"
          />
        </template>
      </ProductCardBackDeck>
      <ProductCardWishlistDeck
        v-if="wishlistCard"
        :model="model"
        :productName="productName"
        :href="link"
        :small="wishlistCardSmall"
        :addingToBag="mixin_addToBag_adding"
        :currentVariant="currentVariant"
        :sizeVariantIndex="sizeVariantIndex"
        :variants="variants"
        :loading="wishlistUpdating"
        :fontColorOverride="fontColorOverride"
        :outlet="outlet"
        :noPrice="noPrice"
        :showQuickBuy="showQuickBuy"
        :editable="editable"
        @on-quick-buy="quickAddDesktop"
        @on-click-size="sizeSelectorOverlay = 'wishlist'"
        @on-click-color="colorSelectorOverlay = true"
        @on-notify="showNotifyOverlay = true"
      />
    </section>
    <PrePurchaseConfirm
      v-if="isPrePurchase"
      :model="model"
      :sku="selectedSizeObject.Sku"
      :currentVariant="currentVariant"
      :visible="showPrePurchaseConfirm"
      @close="showPrePurchaseConfirm = false"
    />
    <Modal
      v-if="!miniCard"
      :visible="mixin_addToBag_viewOverlay"
      :title="insertString('Product', 'ProductAddedToBag')"
      :gridCols="$mqAliases.xl ? 6 : $mqAliases.md || $mqAliases.lg ? 6 : 12"
      :scrollLock="true"
      :verticalCenter="true"
      :compact="true"
      @close="mixin_addToBag_viewOverlay = false"
    >
      <AddedToBagSummary />
    </Modal>
  </component>
</template>
<script>

import Api from '@/Scripts/api/api';
import { mapState } from 'vuex';
import productComputedMixin from '@/Scripts/lib/mixins/productComputedMixin.js';
import { eventBus } from '@/Scripts/lib/helper/eventBus.js';

import GenderHelper from '@/Scripts/lib/helper/genderHelper.js';
import addToBagMixin from '@/Scripts/lib/mixins/addToBagmixin.js';
import productCardTrackingMixin from '@/Scripts/components/productcards/ProductCard/Mixins/productCardTrackingMixin.js';
import ProductTopSlider from '@/Scripts/components/pages/ProductV2/Blocks/ShopBlockAssets/ProductTopSlider.vue';
import ProductCardFrontDeck from '@/Scripts/components/productcards/ProductCard/Assets/ProductCardFrontDeck.vue';
import SvgComponent from '@/Scripts/components/shared/svg/Svg.vue';
import AddedToBagSummary from '@/Scripts/components/layout/Bag/Assets/AddedToBagSummary.vue';
import Modal from '@/Scripts/components/shared/Modal/ModalNew.vue';
const ProductCardBackDeck = () => import('@/Scripts/components/productcards/ProductCard/Assets/ProductCardBackDeck.vue');
const ProductCardWishlistDeck = () => import('@/Scripts/components/productcards/ProductCard/Assets/ProductCardWishlistDeck.vue');
const SelectSizeModule = () => import('@/Scripts/components/productcards/ProductCard/Assets/SelectSizeModule.vue');
const SelectColorModule = () => import('@/Scripts/components/productcards/ProductCard/Assets/SelectColorModule.vue');

const WishlistButton = () => import('@/Scripts/components/productcards/ProductCard/Assets/WishlistButton.vue');

const Notify = () => import(/* webpackChunkName: "Notify" */'@/Scripts/components/pages/ProductV2/Blocks/BlockAssets/Notify.vue');
const PrePurchaseConfirm = () => import('@/Scripts/components/shared/PrePurchase/PrePurchaseConfirm.vue');

export default {
  name: 'ProductCard',
  mixins: [productComputedMixin, addToBagMixin, productCardTrackingMixin],
  components: {
    ProductTopSlider,
    ProductCardFrontDeck,
    SvgComponent,
    ProductCardBackDeck,
    ProductCardWishlistDeck,
    SelectSizeModule,
    SelectColorModule,
    Notify,
    PrePurchaseConfirm,
    AddedToBagSummary,
    Modal,
    WishlistButton
  },
  props: {
    model: Object,
    index: Number,
    outlet: Boolean,
    archived: Boolean,
    startVariantColor: {
      type: String,
      default: ''
    },
    presetSize: String,
    cols: Array,
    deactivateMobileSwipe: Boolean,
    forcePackShot: Boolean,
    noLazyImage: Boolean,
    wishlistVariants: Array,
    fontColorOverride: {
      type: String,
      default: ''
    },
    trackSourceObject: [Object, String],
    recoVersion: String,
    showQuickBuy: {
      type: Boolean,
      default: true
    },
    wishlistCard: Boolean,
    wishlistCardSmall: Boolean,
    forceMiniWishlistCard: Boolean,
    editable: Boolean,
    disableClick: Boolean,
    listName: String,
    noPrice: Boolean,
    userIsTabbing: Boolean,
    featureLookImages: Boolean
  },
  data () {
    return {
      sizeVariantIndex: -1,
      sizeWarning: false,
      mouseOver: false,
      sizeSelectorOverlay: 'hidden',
      colorSelectorOverlay: false,
      showNotifyOverlay: false,
      showPrePurchaseConfirm: false,
      reloadImage: false,
      colorVariantIndex: 0,
      packShotMode: 0,
      prePurchaseAvailableCountry: {
        fetching: false,
        available: false
      },
      wishlistUpdating: false,
      viewMoreMenuOpen: false
    };
  },
  watch: {
    wishlistVariants: {
      deep: true,
      handler (val) {
        this.wishlistUpdating = false;
      }
    },
    currentVariantSizes: {
      immediate: true,
      handler (arr) {
        if (arr.length === 1) {
          this.sizeVariantIndex = 0;
        }
      }
    },
    sizeSelectorOverlay (val) {
      if (val === 'hidden') {
        this.sizeWarning = false;
      }
    },
    startVariantColor () {
      this.setColorIndexOnLoad();
    },
    presetSize: {
      immediate: true,
      handler (val) {
        if (val) {
          this.setPresetSize();
        }
      }
    }
  },
  computed: {
    /* */
    /* imported from productComputedMixin: */
    /* productName,variants, currentVariant, currentVariantPrice, selectedSizeObject, selectedSizeOnWishlist, hasSizeOnWishlist */
    /* */

    ...mapState({
      isLoggedIn: state => state.appContext.isLoggedIn,
      isLoyalty: state => state.appContext.isLoyalty
    }),
    productHasLookImages () {
      return this.variants.some(variant => {
        return variant.LookImages && variant.LookImages.length > 0;
      });
    },

    imageSettings () {
      const padding = this.model && this.model.Category === 'Tent' ? 3 : null;
      return {
        initWidth: 180,
        proportion: this.featureLookImages && this.productHasLookImages ? 0.5 : 0.8,
        bgColor: this.featureLookImages && this.productHasLookImages ? '#c5c5c5' : '#F5F5F5',
        packShotPaddingOverride: padding,
        hideLookimages: !this.featureLookImages || !this.productHasLookImages,
        hideVideo: this.featureLookImages && this.productHasLookImages,
        hidePackShots: this.featureLookImages && this.productHasLookImages
      };
    },
    miniWishlistCard () {
      return this.wishlistCard && (this.miniCard || this.forceMiniWishlistCard);
    },
    miniCard () {
      return this.isMobile;
    },
    isMobile () {
      return this.$mqAliases.xs || this.$mqAliases.sm;
    },
    outletAccess () {
      return !this.outlet || (this.isLoyalty && this.isLoggedIn);
    },
    link () {
      if (!this.currentVariant || this.model.NoLink || this.disableClick) {
        return '';
      }
      let params = '';
      if (GenderHelper.getGender() === 'women') {
        params += '&women=true';
      }
      if (this.featureLookImages && this.productHasLookImages) {
        params += '&feature=look';
      }
      return `${this.currentVariant.Url}${params}`;
    },
    isPrePurchase () {
      return this.model.PrePurchase && this.prePurchaseAvailableCountry.available;
    }

  },
  methods: {
    updateSelectedSizeVariant (e) {
      if (this.sizeSelectorOverlay === 'bag') {
        this.onQuickAdd(e);
      } else if (this.sizeSelectorOverlay === 'wishlist') {
        this.sizeVariantIndex = e;
        this.sizeSelectorOverlay = 'hidden';
        this.wishlistUpdating = true;
        this.$emit('on-size-change', this.currentVariantSizes[e].Size);
      }
    },
    updateSelectedColorVariant (e) {
      this.colorVariantIndex = e;
      this.wishlistUpdating = true;
      this.$emit('on-color-change', this.variants[e].ColorCode);
    },
    quickAddDesktop () {
      if (this.sizeVariantIndex > -1) {
        this.onQuickAdd(this.sizeVariantIndex);
      } else {
        this.sizeWarning = true;
        this.sizeSelectorOverlay = 'bag';
      }
    },
    onQuickAdd (sizeIndex) {
      if (!this.miniCard) {
        this.sizeSelectorOverlay = 'hidden';
      }
      if (sizeIndex === -1) {
        this.sizeWarning = true;
      } else {
        this.sizeWarning = false;
        this.sizeVariantIndex = sizeIndex;
        this.mixin_addToBag_add('quick_buy_add', this.currentVariant, this.selectedSizeObject);
      }
    },
    setColorIndexOnLoad () {
      const color = this.startVariantColor ? this.startVariantColor : this.model.MainColor;
      const index = this.variants.findIndex(v => v.ColorCode === color);
      this.colorVariantIndex = index > -1 ? index : 0;
    },
    setPresetSize () {
      if (this.presetSize) {
        const index = this.currentVariantSizes.findIndex(x => x.Size === this.presetSize);
        if (index > -1) {
          this.sizeVariantIndex = index;
        }
      }
    }

  },
  async created () {
    this.setColorIndexOnLoad();
    this.setPresetSize();

    // prepurhcase availibility
    if (this.model.PrePurchase) {
      try {
        this.prePurchaseAvailableCountry.fetching = true;
        const response = await Api.getContentByFriendlyUrl('/api/product/PrePurchaseAvailable');
        this.prePurchaseAvailableCountry.available = response.data;
        this.prePurchaseAvailableCountry.fetching = false;
      } catch (e) {
        this.prePurchaseAvailableCountry.fetching = false;
      }
    };
    eventBus.$on('WISHLIST_SIZE_CHANGED', data => {
      if (data && data.code === this.model.Code && data.colorCode === this.currentVariant.ColorCode) {
        this.sizeVariantIndex = data.sizeIndex;
      }
    });
  }

};
</script>
<style lang="scss">
@import "@/scss/defaults.scss";
.productcard {
  display: grid;
  grid-template-columns: 1fr;
  // row-gap: 7px;
  &-section-top {
    .productcard-heart {
      position: absolute;
      right: 0;
      z-index: 300;
    }
  }
  &-section-bottom {
    .wishlist-details &,
    .product-guide-body &,
    .product-row &,
    .product-slider &,
    .product-card-list & {
      overflow: hidden;
    }
    &.-outlet {
      // overflow: visible;
    }
    // Reusable flex settings for footer
    .flex-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .price-colors {
      justify-content: flex-start;
      margin-top: 7px;
      @include media-breakpoint-up(md) {
        height: 30px;
      }
    }
    .productcard-backdeck {
      .price-colors {
        @include media-breakpoint-up(md) {
          height: 32px;
        }
      }
      .price-colors-price {
        margin-top: 1.5px;
      }
    }
  }
  .overlay-bounding-box,
  &-section-top {
    grid-row: 1/2;
    grid-column: 1/2;
    position: relative;
  }
  // Container that holds all the overlays.
  .overlay-bounding-box {
    margin: 0;
    pointer-events: none; // Makes sure the container itself does not prevent clicks and swipes on photos.
    .overlay-container {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: $white;
      color: #1d1d1d;
      padding: 12px 0;
      z-index: 5001;
      pointer-events: all;
    }
    button.close {
      background-color: transparent;
      border: 0;
      padding: 0;
      margin: 0;
      text-transform: none;
      font-variant: normal;
      outline: 0;
      border-radius: 0;
      height: auto;
      display: flex;
      justify-content: flex-start;
      position: absolute;
      top: 15px;
      right: 15px;
    }
  }
}
</style>
